import CampanaNotificaciones from "./CampanaNotificaciones";
import BotonInstructivo from "./BotonInstructivo";
import BotonMapaProceso from "./BotonMapaProceso";
import { useState, useMemo } from "react";
import { supabase } from './supabase';
import { subirDatos } from './syncSupabase';
import { guardarNotificacion } from './NotificadorALQOS';
import Comunicados from './Comunicados';
import Onboarding from './Onboarding';
import PerfilesPuesto from './PerfilesPuesto';

const STORAGE_RRHH = 'alqos_rrhh';
const STORAGE_CAPS = 'alqos_caps';
const STORAGE_HS   = 'alqos_hs';

const storage = {
  get: (k) => { try { return JSON.parse(localStorage.getItem(k)||'[]'); } catch { return []; } },
  set: (k,v) => { try { localStorage.setItem(k,JSON.stringify(v)); } catch {} },
};

const estadosEmpleado = {
  activo:     { label:"✅ Activo",       color:"#22c55e" },
  inactivo:   { label:"⛔ Inactivo",     color:"#e63946" },
  licencia:   { label:"🟡 En licencia",  color:"#f59e0b" },
  vacaciones: { label:"🏖️ Vacaciones",  color:"#3b82f6" },
  suspendido: { label:"⏸️ Suspendido",  color:"#808085" },
};

const tiposContrato = ["Relación de dependencia","Tiempo parcial","Pasantía","Contrato eventual","Autónomo / Factura"];
const areasEmpresa  = ["Calidad","Producción","Pre Prensa","Administración","Ventas","Comercio Exterior","Mantenimiento","Higiene y Seguridad","Depósito","Dirección"];
const turnos        = ["Mañana","Tarde","Noche","Rotativo","Administrativo"];
const nivelDesempeño= ["Excelente","Muy bueno","Bueno","Regular","A mejorar"];

// Campos personales — compartidos entre el alta y el modal de edición, así no
// pueden desincronizarse (brief pidió "todos los campos personales que ya
// existen hoy en el formulario de alta"; no existe campo "dirección" en el
// alta real pese a que el brief lo menciona como ejemplo — no se inventa acá).
const CAMPOS_PERSONALES = [
  { key:'nombre', label:'Nombre y apellido *' },
  { key:'dni', label:'DNI' },
  { key:'cuil', label:'CUIL' },
  { key:'fechaNacimiento', label:'Fecha de nacimiento', type:'date' },
  { key:'email', label:'Email' },
  { key:'telefono', label:'Teléfono' },
];

function generarLegajo(lista) { return `LEG-${String(lista.length+1).padStart(4,'0')}`; }
function fechaHoy() { return new Date().toISOString().slice(0,10); }
function formVacio() { return { estado:'activo', tipoContrato:'Relación de dependencia', area:'Producción', turno:'Mañana', fechaIngreso:fechaHoy() }; }

function calcularAntiguedad(fechaIngreso) {
  if (!fechaIngreso) return '—';
  const diff = new Date() - new Date(fechaIngreso);
  const años = Math.floor(diff / (1000*60*60*24*365));
  const meses = Math.floor((diff % (1000*60*60*24*365)) / (1000*60*60*24*30));
  if (años > 0) return `${años} año${años!==1?'s':''} ${meses > 0 ? `y ${meses} mes${meses!==1?'es':''}` : ''}`;
  return `${meses} mes${meses!==1?'es':''}`;
}

function calcularEdad(fechaNacimiento) {
  if (!fechaNacimiento) return null;
  const diff = new Date() - new Date(fechaNacimiento);
  return Math.floor(diff / (1000*60*60*24*365));
}

function diasParaVencer(fecha) {
  if (!fecha) return null;
  return Math.ceil((new Date(fecha) - new Date()) / (1000*60*60*24));
}

export default function RecursosHumanos({ onVolver, oscuro, usuario }) {
  const [empleados, setEmpleados] = useState(() => storage.get(STORAGE_RRHH));
  const [vista,     setVista]     = useState('listado');
  const [seccion,   setSeccion]   = useState('empleados');
  const [form,      setForm]      = useState(formVacio);
  const [detalle,   setDetalle]   = useState(null);
  const [tabDet,    setTabDet]    = useState('datos');
  const [confirm,   setConfirm]   = useState(null);
  const [busqueda,  setBusqueda]  = useState('');
  const [filtroArea,setFiltroArea]= useState('todas');
  const [formEval,  setFormEval]  = useState({ nivel:'Bueno', fecha:fechaHoy() });
  const [formDoc,   setFormDoc]   = useState({ tipo:'', descripcion:'', vencimiento:'' });
  const [recibos,      setRecibos]      = useState([]);
  const [loadingRec,   setLoadingRec]   = useState(false);
  const [formRecibo,   setFormRecibo]   = useState({ mes: new Date().getMonth()+1, anio: new Date().getFullYear(), observaciones:'' });
  const [subiendo,     setSubiendo]     = useState(false);
  const [perfiles, setPerfiles] = useState([]);
  const [formOverride, setFormOverride] = useState("");
  const [mostrarOverride, setMostrarOverride] = useState(false);
  const [mostrarNuevoPuesto, setMostrarNuevoPuesto] = useState(false);
  const [nuevoPuestoNombre, setNuevoPuestoNombre] = useState("");
  const [firmando,     setFirmando]     = useState(null);
  const [showEditarPersonal, setShowEditarPersonal] = useState(false);
  const [editandoId,   setEditandoId]   = useState(null);

  const meses = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

  const recordarFirma = () => {
    const pend = recibos.filter(r => !r.firmado);
    if (!pend.length) { alert("Este empleado no tiene recibos pendientes de firma."); return; }
    if (!window.confirm(`Enviar recordatorio a ${detalle.nombre} por ${pend.length} recibo(s) sin firmar?`)) return;
    guardarNotificacion({
      modulo: "Empleado",
      destinatario_id: detalle.usuario_id || detalle.id,
      tipo: "recordatorio_firma",
      titulo: "Recibos pendientes de firma",
      mensaje: `Tenés ${pend.length} recibo(s) sin firmar. Ingresá a Mis Recibos para revisarlos.`,
    });
    alert("Recordatorio enviado.");
  };

  const cargarPerfiles = async () => {
    try { const { data } = await supabase.from("perfiles_puesto").select("data"); setPerfiles(data ? data.map(x=>x.data) : []); } catch(e) { console.warn(e); }
  };

  const NIVELES_ORDEN = ['Primario incompleto','Primario completo','Secundario incompleto','Secundario completo','Terciario','Universitario','Posgrado'];

  const verificarCompetencia = (emp) => {
    const perfil = perfiles.find(p => p.puesto?.toLowerCase().trim() === emp.puesto?.toLowerCase().trim());
    if (!perfil) return { sinPerfil:true };
    const faltas = [];
    const edad = calcularEdad(emp.fechaNacimiento);
    if (perfil.edadMin && edad!==null && edad < perfil.edadMin) faltas.push(`Edad mínima ${perfil.edadMin} (tiene ${edad})`);
    if (perfil.nivelMin) {
      const reqIdx = NIVELES_ORDEN.indexOf(perfil.nivelMin);
      const empIdx = NIVELES_ORDEN.indexOf(emp.nivelEstudios||"");
      if (empIdx < reqIdx) faltas.push(`Estudios: requiere ${perfil.nivelMin}`);
    }
    if (perfil.expMin && (parseInt(emp.experiencia)||0) < perfil.expMin) faltas.push(`Experiencia: requiere ${perfil.expMin} años`);
    return { perfil, faltas, cumple: faltas.length===0 };
  };

  const guardarOverride = () => {
    if (!formOverride.trim()) { alert("Escribí la justificación."); return; }
    const ov = { motivo: formOverride.trim(), aprobado_por: usuario?.nombre, fecha: new Date().toISOString() };
    const act = empleados.map(e => e.id===detalle.id ? { ...e, competencia_override: ov } : e);
    setEmpleados(act); storage.set(STORAGE_RRHH, act);
    setDetalle(prev => ({ ...prev, competencia_override: ov }));
    subirDatos('alqos_rrhh');
    setFormOverride(""); setMostrarOverride(false);
  };

  const cargarRecibos = async (empleadoId) => {
    setLoadingRec(true);
    const { data, error } = await supabase
      .from('recibos_sueldo')
      .select('*')
      .eq('empleado_id', empleadoId)
      .order('anio', { ascending: false })
      .order('mes',  { ascending: false });
    if (!error && data) setRecibos(data);
    setLoadingRec(false);
  };

  const subirRecibo = async (archivo) => {
    if (!archivo || !detalle) return;
    setSubiendo(true);
    try {
      const path = `${detalle.legajo}/${formRecibo.anio}-${String(formRecibo.mes).padStart(2,'0')}_${Date.now()}.pdf`;
      const { error: upErr } = await supabase.storage
        .from('recibos-sueldo')
        .upload(path, archivo, { contentType: 'application/pdf', upsert: true });
      if (upErr) throw upErr;
      const { error: dbErr } = await supabase.from('recibos_sueldo').insert({
        empleado_id:      detalle.id,
        empleado_nombre:  detalle.nombre,
        legajo:           detalle.legajo,
        mes:              formRecibo.mes,
        anio:             formRecibo.anio,
        archivo_path:     path,
        subido_por:       usuario?.nombre || 'Admin',
        observaciones:    formRecibo.observaciones,
      });
      if (dbErr) throw dbErr;
      await cargarRecibos(detalle.id);
      alert('✅ Recibo subido correctamente');
    } catch (e) {
      alert('Error al subir: ' + e.message);
    }
    setSubiendo(false);
  };

  const descargarRecibo = async (path, nombreArchivo) => {
    const { data, error } = await supabase.storage
      .from('recibos-sueldo')
      .download(path);
    if (error) { alert('Error al descargar'); return; }
    const url = URL.createObjectURL(data);
    const a = document.createElement('a');
    a.href = url; a.download = nombreArchivo; a.click();
    URL.revokeObjectURL(url);
  };

  // eslint-disable-next-line no-unused-vars
  const firmarRecibo = async (recibo) => {
    if (firmando) return;
    setFirmando(recibo.id);
    const { error } = await supabase.from('recibos_sueldo').update({
      firmado:     true,
      firmado_por: usuario?.nombre || detalle.nombre,
      firmado_at:  new Date().toISOString(),
    }).eq('id', recibo.id);
    if (!error) await cargarRecibos(detalle.id);
    setFirmando(null);
  };


  const bg     = oscuro ? '#09184D' : '#F0EEFF';
  const cardBg = oscuro ? '#0d2160' : 'white';
  const texto  = oscuro ? '#EDF2FA' : '#2F3E2E';
  const borde  = oscuro ? '#1e3a8a' : '#eee';
  const navBg  = oscuro ? '#060f30' : '#09184D';
  const sub    = oscuro ? '#9aabd4' : '#808085';
  const esAdmin = usuario?.rol === 'Administrador';

  const inp  = { width:'100%', padding:'8px 12px', borderRadius:'8px', border:`1px solid ${borde}`, fontSize:'13px', boxSizing:'border-box', marginTop:'4px', background:cardBg, color:texto, fontFamily:'sans-serif', outline:'none' };
  const lbl  = { fontSize:'11px', color:sub, fontWeight:'700', display:'block', marginTop:'12px', textTransform:'uppercase', letterSpacing:'.05em' };
  const card = { background:cardBg, borderRadius:'14px', padding:'1.5rem', border:`1px solid ${borde}`, marginBottom:'14px' };

  const caps = storage.get(STORAGE_CAPS);
  const hs   = storage.get(STORAGE_HS);

  const vencimientosProximos = useMemo(() => {
    const alertas = [];
    empleados.forEach(e => {
      if (e.estado !== 'activo') return;
      const checks = [
        { campo: e.vencLibreta,   label: 'Libreta sanitaria' },
        { campo: e.vencArt,       label: 'ART' },
        { campo: e.vencHabilitacion, label: 'Habilitación' },
        { campo: e.vencRevision,  label: 'Revisión médica' },
      ];
      checks.forEach(({ campo, label }) => {
        const dias = diasParaVencer(campo);
        if (dias !== null && dias <= 30) {
          alertas.push({ empleado: e.nombre, legajo: e.legajo, label, dias, fecha: campo });
        }
      });
    });
    return alertas.sort((a,b) => a.dias - b.dias);
  }, [empleados]);

  useMemo(() => { cargarPerfiles(); }, []);

  const resetClaveRecibos = () => {
    if (!detalle) return;
    if (!window.confirm(`Resetear la clave de recibos de ${detalle.nombre}? Tendra que crear una nueva la proxima vez que entre a Mis Recibos.`)) return;
    const act = empleados.map(e => e.id === detalle.id ? { ...e, clave_recibos: null } : e);
    setEmpleados(act);
    storage.set(STORAGE_RRHH, act);
    setDetalle(prev => ({ ...prev, clave_recibos: null }));
    subirDatos('alqos_rrhh');
    alert('Clave de recibos reseteada. El empleado debera crear una nueva.');
  };

  const crearPuestoRapido = async () => {
    const nombre = nuevoPuestoNombre.trim();
    if (!nombre) { alert("Poné el nombre del puesto."); return; }
    if (perfiles.some(p => p.puesto?.toLowerCase().trim() === nombre.toLowerCase())) { alert("Ese puesto ya existe."); return; }
    const p = { id: Date.now(), puesto: nombre, area: form.area||"", edadMin: 18, nivelMin: "", expMin: 0, competencias: "", responsabilidades: "", creado_por: usuario?.nombre, actualizado_at: new Date().toISOString() };
    const tenantId = Number(localStorage.getItem("alqos_tenant_id")) || null;
    try { await supabase.from("perfiles_puesto").upsert([{ id:p.id, data:p, tenant_id:tenantId }]); } catch(e) { console.warn(e); }
    setPerfiles(prev => [p, ...prev]);
    setForm(f => ({ ...f, puesto: nombre }));
    setNuevoPuestoNombre(""); setMostrarNuevoPuesto(false);
    alert("Puesto creado. Completá sus requisitos después en la pestaña Perfiles.");
  };

  const guardar = async () => {
    if (!form.nombre?.trim()) { alert('Nombre obligatorio'); return; }
    let usuario_id = null;
    // Crear acceso al portal empleado (si se completó email + contraseña).
    // Antes esto armaba un "usuario" fantasma SOLO en localStorage (password
    // en texto plano, sin cuenta real en Supabase Auth) que nunca podía
    // loguearse de verdad ni sincronizaba entre dispositivos. Ahora usa el
    // mismo endpoint blindado que Administración > Usuarios (api/crear-
    // usuario.js), que crea la cuenta real en Supabase Auth + la fila en la
    // tabla usuarios. Fix: auditoría Fase 3 Paso 4.
    if (form.acceso_email?.trim() && form.acceso_pass?.trim()) {
      if (form.acceso_pass.length < 6) { alert('La contraseña debe tener al menos 6 caracteres'); return; }
      const { getUsuarios: getU } = require('./GestorUsuarios');
      const usuariosActuales = getU();
      const existe = usuariosActuales.find(u => u.email?.toLowerCase() === form.acceso_email.toLowerCase() || u.celular === form.acceso_email);
      if (existe) {
        alert('Ya existe un usuario con ese email o celular');
        return;
      }
      try {
        const { data: sesion } = await supabase.auth.getSession();
        const token = sesion?.session?.access_token || '';
        const resp = await fetch('/api/crear-usuario', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({
            email: form.acceso_email.trim(),
            password: form.acceso_pass,
            nombre: form.nombre,
            rol: 'Empleado',
            empresa: 'ALQOS™',
            celular: form.acceso_email.trim(),
          }),
        });
        const data = await resp.json();
        if (!resp.ok || data.error) {
          alert('Error creando acceso al portal: ' + (data.error || 'error desconocido'));
          return;
        }
        usuario_id = data.usuario?.id || null;
      } catch (e) {
        alert('Error de conexión creando acceso al portal: ' + e.message);
        return;
      }
    }
    let cv_path = form.cv_path || null;
    if (form._cv) {
      try {
        const lg = generarLegajo(empleados);
        const path = `cv/${lg}_${Date.now()}.pdf`;
        const { error } = await supabase.storage.from('recibos-sueldo').upload(path, form._cv, { upsert:true });
        if (!error) cv_path = path;
      } catch(e) { console.warn('cv:', e); }
    }
    const { _cv, ...formLimpio } = form;
    let nuevos;
    if (editandoId) {
      // Edición: merge sólo de los campos que el modal de edición realmente
      // muestra (CAMPOS_PERSONALES) sobre el registro existente — no todo
      // formLimpio, para no arrastrar ningún campo de otras secciones del
      // form (laboral/formación/vencimientos/portal/CV) que este modal no
      // edita. evaluaciones/documentos/usuario_id/clave_recibos/legajo/etc.
      // quedan intactos porque nunca se tocan acá.
      const soloPersonales = Object.fromEntries(
        CAMPOS_PERSONALES.map(({key}) => [key, formLimpio[key]])
      );
      nuevos = empleados.map(e => e.id === editandoId ? { ...e, ...soloPersonales } : e);
    } else {
      const nuevo = { ...formLimpio, cv_path, id:Date.now(), legajo:generarLegajo(empleados), fechaCreacion:new Date().toISOString(), creadoPor:usuario?.nombre||'Sistema', evaluaciones:[], documentos:[], usuario_id };
      nuevos = [...empleados, nuevo];
    }
    setEmpleados(nuevos);
    storage.set(STORAGE_RRHH, nuevos);
    // Sync a Supabase
    subirDatos('alqos_rrhh');
    if (editandoId) {
      if (detalle?.id === editandoId) setDetalle(nuevos.find(e => e.id === editandoId));
      setEditandoId(null);
      setShowEditarPersonal(false);
      setForm(formVacio());
    } else {
      setForm(formVacio());
      setVista('listado');
    }
  };

  // acceso_email/acceso_pass son campos transitorios que solo sirven para
  // disparar la creación de cuenta de portal en el alta (ver guardar()) — si
  // el registro ya los tiene guardados de un alta anterior y se cargan acá
  // sin filtrar, guardar() intentaría recrear la cuenta de portal al editar
  // solo datos personales y fallaría con "Ya existe un usuario con ese email".
  const abrirEditar = (emp) => {
    // eslint-disable-next-line no-unused-vars
    const { acceso_email, acceso_pass, _cv, ...resto } = emp;
    setForm(resto);
    setEditandoId(emp.id);
    setShowEditarPersonal(true);
  };

  const eliminar = (id) => {
    const nuevos = empleados.filter(e => e.id !== id);
    setEmpleados(nuevos); storage.set(STORAGE_RRHH, nuevos);
    supabase.from('empleados').delete().eq('id', id).then(({error})=>{ if(error) console.warn('sync eliminar empleado:', error); });
    setConfirm(null); setDetalle(null); setVista('listado');
  };

  const cambiarEstado = (id, nuevoEstado) => {
    const act = empleados.map(e => e.id===id ? {...e, estado:nuevoEstado} : e);
    setEmpleados(act); storage.set(STORAGE_RRHH, act);
    subirDatos('alqos_rrhh');
    if (detalle?.id===id) setDetalle(prev=>({...prev, estado:nuevoEstado}));
  };

  const agregarEvaluacion = () => {
    if (!formEval.nivel) return;
    const eval_ = { ...formEval, id:Date.now(), evaluadoPor:usuario?.nombre, fechaRegistro:new Date().toISOString() };
    const act = empleados.map(e => e.id===detalle.id ? {...e, evaluaciones:[...(e.evaluaciones||[]), eval_]} : e);
    setEmpleados(act); storage.set(STORAGE_RRHH, act);
    subirDatos('alqos_rrhh');
    setDetalle(prev=>({...prev, evaluaciones:[...(prev.evaluaciones||[]), eval_]}));
    setFormEval({ nivel:'Bueno', fecha:fechaHoy() });
  };

  const agregarDocumento = async () => {
    if (!formDoc.tipo?.trim()) return;
    let archivo_path = null;
    if (formDoc._file) {
      try {
        const ext = formDoc._file.name.split('.').pop();
        const path = `documentos/${detalle.legajo}/${Date.now()}.${ext}`;
        const { error } = await supabase.storage.from('recibos-sueldo').upload(path, formDoc._file, { upsert:true });
        if (!error) archivo_path = path;
      } catch(e) { console.warn(e); }
    }
    const doc = { tipo:formDoc.tipo, descripcion:formDoc.descripcion, vencimiento:formDoc.vencimiento, archivo_path, id:Date.now(), registradoPor:usuario?.nombre, fechaRegistro:new Date().toISOString() };
    const act = empleados.map(e => e.id===detalle.id ? {...e, documentos:[...(e.documentos||[]), doc]} : e);
    setEmpleados(act); storage.set(STORAGE_RRHH, act);
    setDetalle(prev=>({...prev, documentos:[...(prev.documentos||[]), doc]}));
    subirDatos('alqos_rrhh');
    setFormDoc({ tipo:'', descripcion:'', vencimiento:'' });
  };

  const exportarNomina = () => {
    const headers = ['Legajo','Nombre','DNI','CUIL','Puesto','Área','Contrato','Turno','Estado','Ingreso','Antigüedad','Email','Teléfono'];
    const rows = empleados.map(e => [e.legajo,e.nombre,e.dni,e.cuil,e.puesto,e.area,e.tipoContrato,e.turno,e.estado,e.fechaIngreso,calcularAntiguedad(e.fechaIngreso),e.email,e.telefono]);
    const csv = [headers,...rows].map(r=>r.map(v=>`"${v||''}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF'+csv],{type:'text/csv;charset=utf-8;'});
    const a = document.createElement('a'); a.href=URL.createObjectURL(blob); a.download='ALQOS_Nomina.csv'; a.click();
  };

  const empleadosFiltrados = empleados.filter(e => {
    const mA = filtroArea==='todas'||e.area===filtroArea;
    const mB = !busqueda||[e.nombre,e.legajo,e.puesto,e.dni].some(v=>v?.toLowerCase().includes(busqueda.toLowerCase()));
    return mA&&mB;
  });

  const resumen = {
    total:    empleados.length,
    activos:  empleados.filter(e=>e.estado==='activo').length,
    licencia: empleados.filter(e=>e.estado==='licencia').length,
    areas:    [...new Set(empleados.map(e=>e.area))].length,
  };

  // ── CONFIRM ──
  if (confirm) return (
    <div style={{ minHeight:'100vh', background:bg, fontFamily:'sans-serif', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ background:cardBg, borderRadius:'16px', padding:'2rem', maxWidth:'360px', width:'90%', border:`1px solid ${borde}`, textAlign:'center' }}>
        <p style={{ fontSize:'32px' }}>👤</p>
        <p style={{ fontSize:'15px', fontWeight:'600', color:texto }}>¿Eliminar este empleado?</p>
        <p style={{ fontSize:'13px', color:sub, marginBottom:'1.5rem' }}>Esta acción no se puede deshacer.</p>
        <div style={{ display:'flex', gap:'10px', justifyContent:'center' }}>
          <button onClick={()=>setConfirm(null)} style={{ padding:'10px 20px', background:'none', border:`1px solid ${borde}`, borderRadius:'10px', cursor:'pointer', color:texto, fontSize:'13px' }}>Cancelar</button>
          <button onClick={()=>eliminar(confirm)} style={{ padding:'10px 20px', background:'#e63946', border:'none', borderRadius:'10px', cursor:'pointer', color:'white', fontSize:'13px', fontWeight:'600' }}>Eliminar</button>
        </div>
      </div>
    </div>
  );

  // ── DETALLE ──
  if (vista==='detalle'&&detalle) {
    const capsEmp    = caps.filter(c=>c.empleado===detalle.nombre||c.participantes?.includes(detalle.nombre)||c.asistentes?.some(a=>a.nombre===detalle.nombre));
    const incEmp     = hs.filter(h=>h.empleado===detalle.nombre||h.afectado===detalle.nombre);
    const ultimaEval = detalle.evaluaciones?.slice(-1)[0];
    const colorNivel = { Excelente:'#22c55e', 'Muy bueno':'#3b82f6', Bueno:'#7B5BF2', Regular:'#f59e0b', 'A mejorar':'#e63946' };

    return (
      <div style={{ minHeight:'100vh', background:bg, fontFamily:'sans-serif' }}>
        <div style={{ background:navBg, padding:'1rem 1.5rem', display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, zIndex:100, marginBottom:'1.5rem' }}>
          <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
            <button onClick={()=>{setDetalle(null);setVista('listado');setTabDet('datos');}} style={{ background:'rgba(255,255,255,0.1)', border:'none', color:'white', padding:'6px 14px', borderRadius:'10px', cursor:'pointer', fontSize:'13px' }}>← Volver</button>
            <span style={{ color:'white', fontWeight:'700', fontSize:'16px' }}>{detalle.legajo} — {detalle.nombre}</span>
          </div>
          {esAdmin&&(
            <div style={{ display:'flex', gap:'8px' }}>
              <button onClick={()=>abrirEditar(detalle)} style={{ background:'#1C3FAA', color:'white', border:'none', borderRadius:'10px', padding:'6px 14px', fontSize:'12px', fontWeight:'600', cursor:'pointer' }}>✏️ Editar</button>
              <button onClick={()=>setConfirm(detalle.id)} style={{ background:'rgba(230,57,70,.2)', border:'1px solid rgba(230,57,70,.4)', color:'#e63946', padding:'6px 14px', borderRadius:'10px', cursor:'pointer', fontSize:'12px' }}>🗑 Eliminar</button>
            </div>
          )}
        </div>

        {showEditarPersonal && (
          <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
            <div style={{ background:cardBg, borderRadius:'16px', padding:'24px', width:'420px', maxWidth:'90%', boxShadow:'0 20px 60px rgba(0,0,0,0.3)' }}>
              <h3 style={{ fontSize:'15px', fontWeight:'700', color:'#2A7FFF', marginBottom:'16px' }}>✏️ Editar datos personales</h3>
              {CAMPOS_PERSONALES.map(({key,label,type})=>(
                <div key={key}>
                  <span style={lbl}>{label}</span>
                  <input type={type||'text'} value={form[key]||''} onChange={e=>setForm(f=>({...f,[key]:e.target.value}))} style={inp}/>
                </div>
              ))}
              <div style={{ display:'flex', gap:'10px', marginTop:'18px' }}>
                <button onClick={()=>{setShowEditarPersonal(false);setEditandoId(null);setForm(formVacio());}} style={{ flex:1, padding:'10px', borderRadius:'10px', border:`1px solid ${borde}`, background:'none', color:sub, fontSize:'13px', cursor:'pointer' }}>Cancelar</button>
                <button onClick={guardar} style={{ flex:2, padding:'10px', borderRadius:'10px', border:'none', background:'#1C3FAA', color:'white', fontSize:'13px', fontWeight:'700', cursor:'pointer' }}>💾 Guardar cambios</button>
              </div>
            </div>
          </div>
        )}

        {/* BADGE ESTADO + ANTIGÜEDAD */}
        <div style={{ padding:'0 2rem', maxWidth:'700px', margin:'0 auto' }}>
          <div style={{ display:'flex', gap:'10px', marginBottom:'14px', flexWrap:'wrap' }}>
            <div style={{ padding:'8px 16px', borderRadius:'20px', background:`${estadosEmpleado[detalle.estado]?.color}22`, border:`1px solid ${estadosEmpleado[detalle.estado]?.color}`, fontSize:'12px', fontWeight:'700', color:estadosEmpleado[detalle.estado]?.color }}>
              {estadosEmpleado[detalle.estado]?.label}
            </div>
            <div style={{ padding:'8px 16px', borderRadius:'20px', background:oscuro?'#0a1f5c':'#f0eeff', border:'1px solid #7B5BF2', fontSize:'12px', fontWeight:'700', color:'#7B5BF2' }}>
              📅 {calcularAntiguedad(detalle.fechaIngreso)}
            </div>
            {ultimaEval&&<div style={{ padding:'8px 16px', borderRadius:'20px', background:`${colorNivel[ultimaEval.nivel]||'#808085'}22`, border:`1px solid ${colorNivel[ultimaEval.nivel]||'#808085'}`, fontSize:'12px', fontWeight:'700', color:colorNivel[ultimaEval.nivel]||'#808085' }}>
              ⭐ {ultimaEval.nivel}
            </div>}
          </div>

          {/* TABS DETALLE */}
          <div style={{ display:'flex', borderBottom:`1px solid ${borde}`, background:cardBg, borderRadius:'12px 12px 0 0', marginBottom:'14px', overflow:'hidden' }}>
            {[['datos','👤 Datos'],['desempeno','⭐ Desempeño'],['docs','📄 Documentos'],['caps','🎓 Capacitaciones'],['incidentes','🦺 Incidentes'],['recibos','💵 Recibos']].map(([k,label])=>(
              <button key={k} onClick={()=>setTabDet(k)}
                style={{ padding:'10px 14px', border:'none', borderBottom:tabDet===k?`3px solid #7B5BF2`:'3px solid transparent', background:'none', cursor:'pointer', fontSize:'12px', fontWeight:tabDet===k?'700':'500', color:tabDet===k?'#7B5BF2':sub }}>
                {label}
              </button>
            ))}
          </div>

          {/* TAB DATOS */}
          {tabDet==='datos'&&(
            <>
              <div style={{ ...card, background:oscuro?'#0a1f5c':'#eff6ff', border:'1px solid #2A7FFF30' }}>
                <h3 style={{ fontSize:'13px', fontWeight:'700', color:'#2A7FFF', marginBottom:'12px', textTransform:'uppercase' }}>Datos personales</h3>
                {[['Nombre',detalle.nombre],['DNI',detalle.dni],['CUIL',detalle.cuil],['Fecha nacimiento',detalle.fechaNacimiento],['Edad',calcularEdad(detalle.fechaNacimiento)?calcularEdad(detalle.fechaNacimiento)+' años':null],['Email',detalle.email],['Teléfono',detalle.telefono]].map(([k,v])=>v&&(
                  <div key={k} style={{ display:'flex', justifyContent:'space-between', fontSize:'13px', padding:'5px 0', borderBottom:`0.5px solid ${borde}` }}>
                    <span style={{ color:sub }}>{k}</span><span style={{ color:texto, fontWeight:'500' }}>{v}</span>
                  </div>
                ))}
              </div>
              <div style={{ ...card, background:oscuro?'#0a1f5c':'#faf5ff', border:'1px solid #7B5BF230' }}>
                <h3 style={{ fontSize:'13px', fontWeight:'700', color:'#7B5BF2', marginBottom:'12px', textTransform:'uppercase' }}>Datos laborales</h3>
                {[['Legajo',detalle.legajo],['Puesto',detalle.puesto],['Área',detalle.area],['Turno',detalle.turno],['Contrato',detalle.tipoContrato],['Categoría CCT',detalle.categoriaCCT],['ART',detalle.art],['Fecha ingreso',detalle.fechaIngreso],['Antigüedad',calcularAntiguedad(detalle.fechaIngreso)],['Responsable',detalle.responsable]].map(([k,v])=>v&&(
                  <div key={k} style={{ display:'flex', justifyContent:'space-between', fontSize:'13px', padding:'5px 0', borderBottom:`0.5px solid ${borde}` }}>
                    <span style={{ color:sub }}>{k}</span><span style={{ color:texto, fontWeight:'500' }}>{v}</span>
                  </div>
                ))}
              </div>
              <div style={{ ...card, background:oscuro?'#071a0a':'#f0fdf4', border:'1px solid #22c55e30' }}>
                <h3 style={{ fontSize:'13px', fontWeight:'700', color:'#22c55e', marginBottom:'12px', textTransform:'uppercase' }}>Formación y competencias</h3>
                {[['Nivel de estudios',detalle.nivelEstudios],['Título / Carrera',detalle.titulo],['Experiencia',detalle.experiencia?detalle.experiencia+' años':null],['Competencias',detalle.competencias]].map(([k,v])=>v&&(
                  <div key={k} style={{ display:'flex', justifyContent:'space-between', fontSize:'13px', padding:'5px 0', borderBottom:`0.5px solid ${borde}`, gap:'12px' }}>
                    <span style={{ color:sub, flexShrink:0 }}>{k}</span><span style={{ color:texto, fontWeight:'500', textAlign:'right' }}>{v}</span>
                  </div>
                ))}
                {!detalle.nivelEstudios && !detalle.titulo && !detalle.experiencia && !detalle.competencias && <p style={{ fontSize:'12px', color:sub, margin:0 }}>Sin datos de formación cargados</p>}
                {detalle.cv_path && (
                  <button onClick={()=>descargarRecibo(detalle.cv_path, `CV_${detalle.nombre}.pdf`)} style={{ marginTop:'12px', padding:'8px 14px', background:'#2A7FFF', color:'white', border:'none', borderRadius:'8px', cursor:'pointer', fontSize:'12px', fontWeight:'600' }}>📥 Descargar CV</button>
                )}
              </div>
              {(() => {
                const v = verificarCompetencia(detalle);
                const ov = detalle.competencia_override;
                const colorH = v.sinPerfil ? sub : (v.cumple ? '#22c55e' : (ov ? '#2A7FFF' : '#e63946'));
                return (
                  <div style={{ ...card, border:`1.5px solid ${colorH}40` }}>
                    <h3 style={{ fontSize:'13px', fontWeight:'700', color:colorH, marginBottom:'10px', textTransform:'uppercase' }}>Verificación de competencia (ISO 9001 §7.2)</h3>
                    {v.sinPerfil ? (
                      <p style={{ fontSize:'12px', color:sub, margin:0 }}>⚠️ No hay perfil definido para el puesto "{detalle.puesto||'—'}". Crealo en la pestaña Perfiles.</p>
                    ) : v.cumple ? (
                      <p style={{ fontSize:'13px', color:'#22c55e', fontWeight:'700', margin:0 }}>✅ Cumple todos los requisitos del puesto</p>
                    ) : ov ? (
                      <div>
                        <p style={{ fontSize:'13px', color:'#2A7FFF', fontWeight:'700', margin:'0 0 6px' }}>✅ Apto con justificación</p>
                        <p style={{ fontSize:'12px', color:texto, margin:'0 0 4px' }}><b>Motivo:</b> {ov.motivo}</p>
                        <p style={{ fontSize:'11px', color:sub, margin:0 }}>Aprobado por {ov.aprobado_por} · {new Date(ov.fecha).toLocaleDateString('es-AR')}</p>
                      </div>
                    ) : (
                      <div>
                        <p style={{ fontSize:'13px', color:'#e63946', fontWeight:'700', margin:'0 0 6px' }}>⚠️ No cumple:</p>
                        <ul style={{ margin:'0 0 10px', paddingLeft:'18px' }}>{v.faltas.map((f,i)=><li key={i} style={{ fontSize:'12px', color:texto }}>{f}</li>)}</ul>
                        {esAdmin && !mostrarOverride && <button onClick={()=>setMostrarOverride(true)} style={{ padding:'8px 14px', background:'#2A7FFF', color:'white', border:'none', borderRadius:'10px', cursor:'pointer', fontSize:'12px', fontWeight:'700' }}>Registrar excepción justificada</button>}
                        {esAdmin && mostrarOverride && (
                          <div>
                            <textarea rows={2} value={formOverride} onChange={e=>setFormOverride(e.target.value)} placeholder="Ej: acredita 10 años de experiencia y competencia demostrada en el puesto" style={{ width:'100%', padding:'10px', borderRadius:'10px', border:`1.5px solid ${borde}`, fontSize:'12px', boxSizing:'border-box', resize:'vertical', color:texto, background:cardBg }}/>
                            <div style={{ display:'flex', gap:'8px', marginTop:'8px' }}>
                              <button onClick={guardarOverride} style={{ flex:1, padding:'9px', background:'#22c55e', color:'white', border:'none', borderRadius:'8px', cursor:'pointer', fontSize:'12px', fontWeight:'700' }}>Guardar justificación</button>
                              <button onClick={()=>{setMostrarOverride(false);setFormOverride("");}} style={{ padding:'9px 14px', background:cardBg, color:sub, border:`1px solid ${borde}`, borderRadius:'8px', cursor:'pointer', fontSize:'12px' }}>Cancelar</button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}
              <div style={{ ...card, background:oscuro?'#0a1f5c':'#f0eeff', border:'1px solid #7B5BF230' }}>
                <h3 style={{ fontSize:'13px', fontWeight:'700', color:'#7B5BF2', marginBottom:'12px', textTransform:'uppercase' }}>Seguridad — Clave de recibos</h3>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', fontSize:'13px', flexWrap:'wrap', gap:'10px' }}>
                  <span style={{ color: detalle.clave_recibos ? '#22c55e' : sub, fontWeight:'500' }}>
                    {detalle.clave_recibos ? '🔒 Clave configurada por el empleado' : '○ Sin clave configurada aun'}
                  </span>
                  <button onClick={resetClaveRecibos} disabled={!detalle.clave_recibos}
                    style={{ padding:'8px 14px', borderRadius:'10px', border:'none', background: detalle.clave_recibos ? '#7B5BF2' : '#cbd5e1', color:'white', fontSize:'12px', fontWeight:'700', cursor: detalle.clave_recibos ? 'pointer' : 'default' }}>
                    🔓 Resetear clave
                  </button>
                </div>
              </div>
              <div style={{ ...card, background:oscuro?'#1a0a00':'#fffbeb', border:'1px solid #f59e0b40' }}>
                <h3 style={{ fontSize:'13px', fontWeight:'700', color:'#f59e0b', marginBottom:'12px', textTransform:'uppercase' }}>Vencimientos</h3>
                {[['Libreta sanitaria',detalle.vencLibreta],['Revisión médica',detalle.vencRevision],['ART vencimiento',detalle.vencArt],['Habilitación',detalle.vencHabilitacion]].map(([k,v])=>{
                  const dias = diasParaVencer(v);
                  const color = dias===null?sub:dias<0?'#e63946':dias<=10?'#e63946':dias<=30?'#f59e0b':'#22c55e';
                  return (
                    <div key={k} style={{ display:'flex', justifyContent:'space-between', fontSize:'13px', padding:'5px 0', borderBottom:`0.5px solid ${borde}` }}>
                      <span style={{ color:sub }}>{k}</span>
                      <span style={{ color, fontWeight:'600' }}>{v||'—'}{dias!==null&&` (${dias<0?'vencido hace '+Math.abs(dias):dias+' días'})`}</span>
                    </div>
                  );
                })}
              </div>
              <div style={card}>
                <h3 style={{ fontSize:'13px', fontWeight:'700', color:texto, marginBottom:'12px', textTransform:'uppercase' }}>Estado</h3>
                <div style={{ display:'flex', gap:'6px', flexWrap:'wrap' }}>
                  {Object.entries(estadosEmpleado).map(([val,{label,color}])=>(
                    <button key={val} onClick={()=>esAdmin&&cambiarEstado(detalle.id,val)}
                      style={{ padding:'7px 12px', borderRadius:'10px', fontSize:'11px', cursor:esAdmin?'pointer':'default', fontWeight:'600', border:detalle.estado===val?`2px solid ${color}`:`1px solid ${borde}`, background:detalle.estado===val?`${color}22`:cardBg, color:detalle.estado===val?color:sub }}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* TAB DESEMPEÑO */}
          {tabDet==='desempeno'&&(
            <>
              {esAdmin&&(
                <div style={card}>
                  <h3 style={{ fontSize:'13px', fontWeight:'700', color:'#7B5BF2', marginBottom:'12px' }}>Nueva evaluación</h3>
                  <span style={lbl}>Nivel de desempeño</span>
                  <div style={{ display:'flex', gap:'6px', flexWrap:'wrap', marginTop:'6px' }}>
                    {nivelDesempeño.map(n=>(
                      <button key={n} onClick={()=>setFormEval(f=>({...f,nivel:n}))}
                        style={{ padding:'6px 12px', borderRadius:'8px', fontSize:'11px', cursor:'pointer', fontWeight:'600', border:formEval.nivel===n?`2px solid ${colorNivel[n]}`:`1px solid ${borde}`, background:formEval.nivel===n?`${colorNivel[n]}22`:cardBg, color:formEval.nivel===n?colorNivel[n]:sub }}>
                        {n}
                      </button>
                    ))}
                  </div>
                  <span style={lbl}>Fecha</span>
                  <input type='date' value={formEval.fecha} onChange={e=>setFormEval(f=>({...f,fecha:e.target.value}))} style={inp}/>
                  <span style={lbl}>Comentarios</span>
                  <textarea rows={2} value={formEval.comentarios||''} onChange={e=>setFormEval(f=>({...f,comentarios:e.target.value}))} style={{...inp,resize:'none'}}/>
                  <button onClick={agregarEvaluacion} style={{ width:'100%', marginTop:'12px', padding:'10px', background:'#7B5BF2', color:'white', border:'none', borderRadius:'10px', cursor:'pointer', fontWeight:'700', fontSize:'13px' }}>
                    + Registrar evaluación
                  </button>
                </div>
              )}
              {(detalle.evaluaciones||[]).length===0
                ? <div style={{ textAlign:'center', padding:'3rem', color:sub }}><p style={{ fontSize:'32px' }}>⭐</p><p>Sin evaluaciones registradas</p></div>
                : [...detalle.evaluaciones].reverse().map((ev,i)=>(
                    <div key={ev.id||i} style={{ ...card, border:`1px solid ${colorNivel[ev.nivel]||'#808085'}40` }}>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'8px' }}>
                        <span style={{ fontSize:'13px', fontWeight:'700', color:colorNivel[ev.nivel]||'#808085' }}>⭐ {ev.nivel}</span>
                        <span style={{ fontSize:'11px', color:sub }}>{ev.fecha} · {ev.evaluadoPor}</span>
                      </div>
                      {ev.comentarios&&<p style={{ fontSize:'13px', color:texto, margin:0, lineHeight:'1.6' }}>{ev.comentarios}</p>}
                    </div>
                  ))
              }
            </>
          )}

          {/* TAB DOCUMENTOS */}
          {tabDet==='docs'&&(
            <>
              {esAdmin&&(
                <div style={card}>
                  <h3 style={{ fontSize:'13px', fontWeight:'700', color:'#3b82f6', marginBottom:'12px' }}>Agregar documento</h3>
                  <span style={lbl}>Tipo de documento</span>
                  <input value={formDoc.tipo} onChange={e=>setFormDoc(f=>({...f,tipo:e.target.value}))} style={inp} placeholder='Ej: Contrato, DNI, Certificado médico'/>
                  <span style={lbl}>Descripción</span>
                  <input value={formDoc.descripcion} onChange={e=>setFormDoc(f=>({...f,descripcion:e.target.value}))} style={inp} placeholder='Detalle del documento'/>
                  <span style={lbl}>Fecha de vencimiento</span>
                  <input type='date' value={formDoc.vencimiento} onChange={e=>setFormDoc(f=>({...f,vencimiento:e.target.value}))} style={inp}/>
                  <span style={lbl}>Archivo (PDF o imagen)</span>
                  <input type='file' accept='application/pdf,image/*' onChange={e=>setFormDoc(f=>({...f,_file:e.target.files[0]}))} style={{...inp, padding:'6px'}}/>
                  <button onClick={agregarDocumento} style={{ width:'100%', marginTop:'12px', padding:'10px', background:'#3b82f6', color:'white', border:'none', borderRadius:'10px', cursor:'pointer', fontWeight:'700', fontSize:'13px' }}>
                    + Registrar documento
                  </button>
                </div>
              )}
              {(detalle.documentos||[]).length===0
                ? <div style={{ textAlign:'center', padding:'3rem', color:sub }}><p style={{ fontSize:'32px' }}>📄</p><p>Sin documentos registrados</p></div>
                : [...detalle.documentos].reverse().map((doc,i)=>{
                    const dias = diasParaVencer(doc.vencimiento);
                    const colorV = dias===null?sub:dias<0?'#e63946':dias<=30?'#f59e0b':'#22c55e';
                    return (
                      <div key={doc.id||i} style={card}>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                          <div>
                            <span style={{ fontSize:'13px', fontWeight:'700', color:'#3b82f6' }}>📄 {doc.tipo}</span>
                            {doc.descripcion&&<p style={{ fontSize:'12px', color:sub, margin:'4px 0 0' }}>{doc.descripcion}</p>}
                          </div>
                          {doc.vencimiento&&<span style={{ fontSize:'11px', fontWeight:'700', color:colorV }}>{doc.vencimiento}{dias!==null&&` (${dias<0?'vencido':dias+' días'})`}</span>}
                        </div>
                        <p style={{ fontSize:'11px', color:sub, margin:'6px 0 0' }}>{doc.registradoPor} · {new Date(doc.fechaRegistro).toLocaleDateString('es-AR')}</p>
                        {doc.archivo_path && <button onClick={()=>descargarRecibo(doc.archivo_path, `${doc.tipo}_${detalle.legajo}`)} style={{ marginTop:'8px', padding:'6px 12px', background:'#2A7FFF', color:'white', border:'none', borderRadius:'8px', cursor:'pointer', fontSize:'11px', fontWeight:'600' }}>📥 Descargar</button>}
                      </div>
                    );
                  })
              }
            </>
          )}

          {/* TAB CAPACITACIONES */}
          {tabDet==='caps'&&(
            capsEmp.length===0
              ? <div style={{ textAlign:'center', padding:'3rem', color:sub }}><p style={{ fontSize:'32px' }}>🎓</p><p>Sin capacitaciones vinculadas</p><p style={{ fontSize:'12px' }}>Las capacitaciones se vinculan desde el módulo Capacitaciones</p></div>
              : capsEmp.map((c,i)=>(
                  <div key={c.id||i} style={card}>
                    <div style={{ display:'flex', justifyContent:'space-between' }}>
                      <div>
                        <span style={{ fontSize:'13px', fontWeight:'700', color:'#7B5BF2' }}>{c.titulo||c.nombre}</span>
                        <p style={{ fontSize:'12px', color:sub, margin:'4px 0 0' }}>{c.tipo||'Capacitación'} · {c.fecha||c.fechaInicio}</p>
                      </div>
                      <span style={{ fontSize:'11px', fontWeight:'700', color:c.estado==='completada'?'#22c55e':'#f59e0b' }}>{c.estado==='completada'?'✅ Completada':'⏳ Pendiente'}</span>
                    </div>
                  </div>
                ))
          )}


          {/* TAB RECIBOS */}
          {tabDet==='recibos'&&(
            <>
              {esAdmin&&(
                <div style={card}>
                  <h3 style={{ fontSize:'13px', fontWeight:'700', color:'#22c55e', marginBottom:'12px' }}>Subir recibo de sueldo</h3>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px' }}>
                    <div>
                      <span style={lbl}>Mes</span>
                      <select value={formRecibo.mes} onChange={e=>setFormRecibo(f=>({...f,mes:parseInt(e.target.value)}))} style={inp}>
                        {meses.map((m,i)=><option key={i} value={i+1}>{m}</option>)}
                      </select>
                    </div>
                    <div>
                      <span style={lbl}>Año</span>
                      <input type='number' value={formRecibo.anio} onChange={e=>setFormRecibo(f=>({...f,anio:parseInt(e.target.value)}))} style={inp} min='2020' max='2030'/>
                    </div>
                  </div>
                  <span style={lbl}>Observaciones</span>
                  <input value={formRecibo.observaciones} onChange={e=>setFormRecibo(f=>({...f,observaciones:e.target.value}))} style={inp} placeholder='Ej: Aguinaldo, adelanto, etc.'/>
                  <span style={lbl}>Archivo PDF</span>
                  <input type='file' accept='application/pdf' onChange={e=>e.target.files[0]&&subirRecibo(e.target.files[0])}
                    style={{...inp, padding:'6px', cursor:'pointer'}}/>
                  {subiendo&&<p style={{ fontSize:'12px', color:'#22c55e', marginTop:'8px', textAlign:'center' }}>⏳ Subiendo archivo...</p>}
                  {recibos.some(r=>!r.firmado)&&(
                    <button onClick={recordarFirma}
                      style={{ width:'100%', marginTop:'12px', padding:'10px', background:'#7B5BF2', color:'white', border:'none', borderRadius:'10px', cursor:'pointer', fontSize:'12px', fontWeight:'700' }}>
                      🔔 Recordar firma pendiente al empleado
                    </button>
                  )}
                </div>
              )}
              {loadingRec
                ? <div style={{ textAlign:'center', padding:'2rem', color:sub }}>⏳ Cargando recibos...</div>
                : recibos.length===0
                  ? <div style={{ textAlign:'center', padding:'3rem', color:sub }}><p style={{ fontSize:'32px' }}>💵</p><p>Sin recibos cargados</p></div>
                  : recibos.map(r=>(
                      <div key={r.id} style={{ ...card, border:`1.5px solid ${r.firmado?'#22c55e40':'#f59e0b40'}`, background: r.firmado?(oscuro?'#0a1a0a':'#f0fdf4'):(oscuro?'#1a0a00':'#fffbeb') }}>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:'10px' }}>
                          <div style={{ flex:1 }}>
                            <span style={{ fontSize:'14px', fontWeight:'700', color:r.firmado?'#22c55e':'#f59e0b' }}>
                              {r.firmado?'✅':'⏳'} {meses[r.mes-1]} {r.anio}
                            </span>
                            {r.observaciones&&<p style={{ fontSize:'12px', color:sub, margin:'4px 0 0' }}>{r.observaciones}</p>}
                            <p style={{ fontSize:'11px', color:sub, margin:'6px 0 0' }}>
                              Subido por {r.subido_por} · {new Date(r.subido_at).toLocaleDateString('es-AR')}
                            </p>
                            {r.firmado&&<p style={{ fontSize:'11px', color:'#22c55e', margin:'3px 0 0', fontWeight:'600' }}>
                              ✍️ Firmado por {r.firmado_por} · {new Date(r.firmado_at).toLocaleString('es-AR')}
                            </p>}
                          </div>
                          <div style={{ display:'flex', flexDirection:'column', gap:'6px', flexShrink:0 }}>
                            <button onClick={()=>descargarRecibo(r.archivo_path, `Recibo_${meses[r.mes-1]}_${r.anio}_${detalle.legajo}.pdf`)}
                              style={{ padding:'7px 12px', background:'#2A7FFF', color:'white', border:'none', borderRadius:'8px', cursor:'pointer', fontSize:'11px', fontWeight:'600' }}>
                              📥 Descargar
                            </button>
                            {!r.firmado&&(
                              <span style={{ fontSize:'11px', color:'#f59e0b', fontWeight:'600', padding:'4px 8px', background:'#fffbeb', borderRadius:'6px', border:'1px solid #f59e0b40' }}>⏳ Pendiente firma empleado</span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
              }
            </>
          )}

          {/* TAB INCIDENTES */}
          {tabDet==='incidentes'&&(
            incEmp.length===0
              ? <div style={{ textAlign:'center', padding:'3rem', color:sub }}><p style={{ fontSize:'32px' }}>🦺</p><p>Sin incidentes registrados</p></div>
              : incEmp.map((h,i)=>(
                  <div key={h.id||i} style={{ ...card, border:'1px solid #e6394640' }}>
                    <div style={{ display:'flex', justifyContent:'space-between' }}>
                      <div>
                        <span style={{ fontSize:'13px', fontWeight:'700', color:'#e63946' }}>🦺 {h.tipo||'Incidente'}</span>
                        <p style={{ fontSize:'12px', color:sub, margin:'4px 0 0' }}>{h.descripcion?.slice(0,60)}</p>
                      </div>
                      <span style={{ fontSize:'11px', color:sub }}>{h.fecha}</span>
                    </div>
                  </div>
                ))
          )}
        </div>
      </div>
    );
  }

  // ── NUEVO EMPLEADO ──
  if (vista==='nueva') return (
    <div style={{ minHeight:'100vh', background:bg, fontFamily:'sans-serif' }}>
      <div style={{ background:navBg, padding:'1rem 1.5rem', display:'flex', alignItems:'center', position:'sticky', top:0, zIndex:100, marginBottom:'1.5rem' }}>
        <button onClick={()=>setVista('listado')} style={{ background:'rgba(255,255,255,0.1)', border:'none', color:'white', padding:'6px 14px', borderRadius:'10px', cursor:'pointer', fontSize:'13px', marginRight:'12px' }}>← Volver</button>
        <span style={{ color:'white', fontWeight:'700', fontSize:'16px' }}>Nuevo Empleado</span>
      </div>
      <div style={{ padding:'0 2rem 2rem', maxWidth:'700px', margin:'0 auto' }}>
        <div style={card}>
          <h3 style={{ fontSize:'14px', fontWeight:'700', color:'#2A7FFF', marginBottom:'12px' }}>Datos personales</h3>
          {CAMPOS_PERSONALES.map(({key,label,type})=>(
            <div key={key}>
              <span style={lbl}>{label}</span>
              <input type={type||'text'} onChange={e=>setForm(f=>({...f,[key]:e.target.value}))} style={inp}/>
            </div>
          ))}
        </div>
        <div style={card}>
          <h3 style={{ fontSize:'14px', fontWeight:'700', color:'#7B5BF2', marginBottom:'12px' }}>Datos laborales</h3>
          <span style={lbl}>Área</span>
          <select onChange={e=>setForm(f=>({...f,area:e.target.value}))} style={inp}>
            {areasEmpresa.map(a=><option key={a}>{a}</option>)}
          </select>
          <span style={lbl}>Turno</span>
          <select onChange={e=>setForm(f=>({...f,turno:e.target.value}))} style={inp}>
            {turnos.map(t=><option key={t}>{t}</option>)}
          </select>
          <span style={lbl}>Tipo de contrato</span>
          <select onChange={e=>setForm(f=>({...f,tipoContrato:e.target.value}))} style={inp}>
            {tiposContrato.map(t=><option key={t}>{t}</option>)}
          </select>
          <span style={lbl}>Puesto / Cargo</span>
          <div style={{ display:'flex', gap:'8px', alignItems:'flex-end' }}>
            {perfiles.length > 0 ? (
              <select value={form.puesto||''} onChange={e=>setForm(f=>({...f,puesto:e.target.value}))} style={{...inp, flex:1}}>
                <option value="">Seleccionar puesto...</option>
                {perfiles.map(p=><option key={p.id} value={p.puesto}>{p.puesto}</option>)}
              </select>
            ) : (
              <input value={form.puesto||''} onChange={e=>setForm(f=>({...f,puesto:e.target.value}))} style={{...inp, flex:1}} placeholder='Creá el primer puesto →'/>
            )}
            <button type="button" onClick={()=>setMostrarNuevoPuesto(v=>!v)} style={{ padding:'10px 14px', background:'#22c55e', color:'white', border:'none', borderRadius:'10px', cursor:'pointer', fontSize:'14px', fontWeight:'700', marginTop:'6px', whiteSpace:'nowrap' }}>+ Puesto</button>
          </div>
          {mostrarNuevoPuesto && (
            <div style={{ marginTop:'10px', padding:'12px', background:oscuro?'#071a0a':'#f0fdf4', borderRadius:'10px', border:'1px solid #22c55e40' }}>
              <span style={lbl}>Nombre del nuevo puesto</span>
              <input value={nuevoPuestoNombre} onChange={e=>setNuevoPuestoNombre(e.target.value)} style={inp} placeholder='Ej: Operario de Producción'/>
              <div style={{ display:'flex', gap:'8px', marginTop:'8px' }}>
                <button type="button" onClick={crearPuestoRapido} style={{ flex:1, padding:'9px', background:'#22c55e', color:'white', border:'none', borderRadius:'8px', cursor:'pointer', fontSize:'12px', fontWeight:'700' }}>Crear y seleccionar</button>
                <button type="button" onClick={()=>{setMostrarNuevoPuesto(false);setNuevoPuestoNombre("");}} style={{ padding:'9px 14px', background:cardBg, color:sub, border:`1px solid ${borde}`, borderRadius:'8px', cursor:'pointer', fontSize:'12px' }}>Cancelar</button>
              </div>
            </div>
          )}
          {[{key:'categoriaCCT',label:'Categoría CCT'},{key:'art',label:'ART'},{key:'fechaIngreso',label:'Fecha de ingreso',type:'date'},{key:'responsable',label:'Responsable / Jefe directo'}].map(({key,label,type})=>(
            <div key={key}>
              <span style={lbl}>{label}</span>
              <input type={type||'text'} defaultValue={key==='fechaIngreso'?fechaHoy():''} onChange={e=>setForm(f=>({...f,[key]:e.target.value}))} style={inp}/>
            </div>
          ))}
          <span style={lbl}>Estado</span>
          <div style={{ display:'flex', gap:'6px', flexWrap:'wrap', marginTop:'6px' }}>
            {Object.entries(estadosEmpleado).map(([val,{label,color}])=>(
              <button key={val} onClick={()=>setForm(f=>({...f,estado:val}))}
                style={{ padding:'6px 12px', borderRadius:'8px', fontSize:'11px', cursor:'pointer', fontWeight:'600', border:form.estado===val?`2px solid ${color}`:`1px solid ${borde}`, background:form.estado===val?`${color}22`:cardBg, color:form.estado===val?color:sub }}>
                {label}
              </button>
            ))}
          </div>
        </div>
        <div style={card}>
          <h3 style={{ fontSize:'14px', fontWeight:'700', color:'#22c55e', marginBottom:'12px' }}>Formación y competencias (ISO 9001 §7.2)</h3>
          <span style={lbl}>Nivel de estudios</span>
          <select value={form.nivelEstudios||''} onChange={e=>setForm(f=>({...f,nivelEstudios:e.target.value}))} style={inp}>
            <option value="">Seleccionar...</option>
            {['Primario incompleto','Primario completo','Secundario incompleto','Secundario completo','Terciario','Universitario','Posgrado'].map(n=><option key={n}>{n}</option>)}
          </select>
          <span style={lbl}>Título / Carrera</span>
          <input value={form.titulo||''} onChange={e=>setForm(f=>({...f,titulo:e.target.value}))} style={inp} placeholder='Ej: Técnico gráfico, Lic. en Administración'/>
          <span style={lbl}>Años de experiencia previa</span>
          <input type='number' min='0' value={form.experiencia||''} onChange={e=>setForm(f=>({...f,experiencia:e.target.value}))} style={inp} placeholder='Ej: 5'/>
          <span style={lbl}>Competencias / habilidades</span>
          <textarea rows={2} value={form.competencias||''} onChange={e=>setForm(f=>({...f,competencias:e.target.value}))} style={{...inp, resize:'vertical'}} placeholder='Ej: manejo de offset, control de calidad, Illustrator'/>
          <span style={lbl}>CV (PDF)</span>
          <input type='file' accept='application/pdf' onChange={e=>setForm(f=>({...f,_cv:e.target.files[0]}))} style={{...inp, padding:'6px'}}/>
        </div>
        <div style={{ ...card, background:oscuro?'#1a0a00':'#fffbeb', border:'1px solid #f59e0b40' }}>
          <h3 style={{ fontSize:'14px', fontWeight:'700', color:'#f59e0b', marginBottom:'12px' }}>Vencimientos a controlar</h3>
          {[{key:'vencLibreta',label:'Libreta sanitaria'},{key:'vencRevision',label:'Revisión médica'},{key:'vencArt',label:'ART'},{key:'vencHabilitacion',label:'Habilitación especial'}].map(({key,label})=>(
            <div key={key}>
              <span style={lbl}>{label}</span>
              <input type='date' onChange={e=>setForm(f=>({...f,[key]:e.target.value}))} style={inp}/>
            </div>
          ))}
        </div>
        <div style={card}>
          <span style={lbl}>Email o celular para acceso</span>
          <input value={form.acceso_email||''} onChange={e=>setForm(f=>({...f,acceso_email:e.target.value}))} style={inp} placeholder='ej: 1123456789 o andrea@mail.com'/>
          <span style={lbl}>Contraseña temporal</span>
          <input type='password' value={form.acceso_pass||''} onChange={e=>setForm(f=>({...f,acceso_pass:e.target.value}))} style={inp} placeholder='Mínimo 6 caracteres'/>
          <p style={{fontSize:'11px',color:sub,margin:'3px 0 0'}}>Se creará automáticamente un usuario con rol Empleado. Desde Administración podés cambiar el rol o permisos.</p>
          <span style={lbl}>Observaciones</span>
          <textarea rows={3} onChange={e=>setForm(f=>({...f,observaciones:e.target.value}))} style={{...inp,resize:'none'}}/>
        </div>
        <button onClick={guardar} style={{ width:'100%', padding:'15px', background:'linear-gradient(135deg,#7B5BF2,#09184D)', color:'white', border:'none', borderRadius:'12px', fontSize:'15px', fontWeight:'700', cursor:'pointer' }}>
          💾 Guardar empleado
        </button>
      </div>
    </div>
  );

  // ── LISTADO ──
  return (
    <div style={{ minHeight:'100vh', background:bg, fontFamily:'sans-serif' }}>
      <div style={{ background:navBg, padding:'1rem 1.5rem', display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, zIndex:100 }}>
        <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
          <button onClick={onVolver} style={{ background:'rgba(255,255,255,0.1)', border:'none', color:'white', padding:'6px 14px', borderRadius:'10px', cursor:'pointer', fontSize:'13px' }}>← Menú</button>
          <BotonInstructivo moduloKey="rrhh" oscuro={oscuro} />
          {usuario?.rol === "Administrador" && <BotonMapaProceso moduloKey="rrhh" oscuro={oscuro} />}
          <span style={{ color:'white', fontWeight:'700', fontSize:'16px' }}>👥 Recursos Humanos</span>
        </div>
        <div style={{ display:'flex', gap:'8px' }}>
          <button onClick={exportarNomina} style={{ padding:'6px 12px', background:'rgba(255,255,255,0.1)', border:'1px solid rgba(255,255,255,0.2)', color:'white', borderRadius:'8px', cursor:'pointer', fontSize:'12px' }}>📊 Nómina CSV</button>
          <CampanaNotificaciones oscuro={oscuro} modulo='RecursosHumanos'/>
          {esAdmin&&<button onClick={()=>{setForm(formVacio());setEditandoId(null);setVista('nueva');}} style={{ padding:'6px 14px', background:'#7B5BF2', color:'white', border:'none', borderRadius:'8px', cursor:'pointer', fontSize:'13px', fontWeight:'600' }}>+ Nuevo</button>}
        </div>
      </div>

      {/* TABS */}
      <div style={{ display:'flex', borderBottom:`1px solid ${borde}`, background:cardBg, padding:'0 1.5rem' }}>
        {[['empleados','👥 Empleados'],['vencimientos','⚠️ Vencimientos'],['organigrama','🏢 Organigrama'],['comunicados','📢 Comunicados'],['onboarding','🚀 Onboarding'],['perfiles','📋 Perfiles']].map(([k,label])=>(
          <button key={k} onClick={()=>setSeccion(k)}
            style={{ padding:'12px 18px', border:'none', borderBottom:seccion===k?`3px solid #7B5BF2`:'3px solid transparent', background:'none', cursor:'pointer', fontSize:'13px', fontWeight:seccion===k?'700':'500', color:seccion===k?'#7B5BF2':sub }}>
            {label}{k==='vencimientos'&&vencimientosProximos.length>0&&<span style={{ marginLeft:'6px', background:'#e63946', color:'white', borderRadius:'50%', fontSize:'10px', padding:'1px 5px' }}>{vencimientosProximos.length}</span>}
          </button>
        ))}
      </div>
      {seccion==='comunicados' && <Comunicados oscuro={oscuro} usuario={usuario} embebido/>}
      {seccion==='onboarding' && <Onboarding oscuro={oscuro} usuario={usuario} embebido/>}
      {seccion==='perfiles' && <PerfilesPuesto oscuro={oscuro} usuario={usuario} embebido/>}

      <div style={{ padding:'1.2rem 1.5rem' }}>
        {/* KPIs */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(130px,1fr))', gap:'10px', marginBottom:'1.5rem' }}>
          {[['Total',resumen.total,'#7B5BF2'],['Activos',resumen.activos,'#22c55e'],['En licencia',resumen.licencia,'#f59e0b'],['Áreas',resumen.areas,'#3b82f6']].map(([lab,val,col])=>(
            <div key={lab} style={{ background:cardBg, borderRadius:'14px', padding:'1rem', border:`1px solid ${borde}`, textAlign:'center' }}>
              <p style={{ fontSize:'24px', fontWeight:'800', color:col, margin:0 }}>{val}</p>
              <p style={{ fontSize:'11px', color:sub, margin:'4px 0 0' }}>{lab}</p>
            </div>
          ))}
        </div>

        {/* TAB EMPLEADOS */}
        {seccion==='empleados'&&(
          <>
            <input placeholder='🔍 Buscar por nombre, legajo, puesto o DNI...' value={busqueda} onChange={e=>setBusqueda(e.target.value)} style={{...inp,marginBottom:'1rem',marginTop:0}}/>
            <div style={{ display:'flex', gap:'6px', flexWrap:'wrap', marginBottom:'1rem' }}>
              <button onClick={()=>setFiltroArea('todas')} style={{ padding:'4px 10px', borderRadius:'16px', fontSize:'11px', cursor:'pointer', fontWeight:'600', border:filtroArea==='todas'?'2px solid #7B5BF2':`1px solid ${borde}`, background:filtroArea==='todas'?'rgba(123,91,242,.1)':cardBg, color:filtroArea==='todas'?'#7B5BF2':sub }}>Todas</button>
              {areasEmpresa.filter(a=>empleados.some(e=>e.area===a)).map(a=>(
                <button key={a} onClick={()=>setFiltroArea(a)} style={{ padding:'4px 10px', borderRadius:'16px', fontSize:'11px', cursor:'pointer', fontWeight:'600', border:filtroArea===a?'2px solid #7B5BF2':`1px solid ${borde}`, background:filtroArea===a?'rgba(123,91,242,.1)':cardBg, color:filtroArea===a?'#7B5BF2':sub }}>{a}</button>
              ))}
            </div>
            {empleadosFiltrados.length===0
              ? <div style={{ textAlign:'center', padding:'5rem 2rem' }}><p style={{ fontSize:'48px' }}>👥</p><p style={{ fontSize:'15px', color:texto, fontWeight:'600' }}>Sin empleados</p></div>
              : empleadosFiltrados.map(e=>{
                  const alerta = vencimientosProximos.some(v=>v.legajo===e.legajo);
                  return (
                    <div key={e.id} onClick={()=>{setDetalle(e);setVista('detalle');setTabDet('datos');cargarRecibos(e.id);}}
                      style={{ background:cardBg, borderRadius:'14px', padding:'1.2rem 1.5rem', border:`1.5px solid ${alerta?'#f59e0b':borde}`, marginBottom:'10px', cursor:'pointer' }}
                      onMouseEnter={ev=>ev.currentTarget.style.boxShadow='0 4px 20px rgba(123,91,242,0.15)'}
                      onMouseLeave={ev=>ev.currentTarget.style.boxShadow='none'}>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                        <div>
                          <span style={{ fontWeight:'700', color:'#7B5BF2', fontSize:'13px' }}>{e.legajo}</span>
                          {' '}<span style={{ fontSize:'13px', color:texto, fontWeight:'600' }}>{e.nombre||'Sin nombre'}</span>
                          {alerta&&<span style={{ marginLeft:'6px', fontSize:'10px', color:'#f59e0b', background:'#fffbeb', padding:'1px 6px', borderRadius:'4px', fontWeight:'700' }}>⚠️ Vence pronto</span>}
                          <p style={{ fontSize:'12px', color:sub, margin:'4px 0 0' }}>{e.puesto||'Sin puesto'} · {e.area} · {e.turno}</p>
                        </div>
                        <div style={{ textAlign:'right' }}>
                          <span style={{ fontSize:'11px', fontWeight:'700', color:estadosEmpleado[e.estado]?.color }}>{estadosEmpleado[e.estado]?.label}</span>
                          <p style={{ fontSize:'11px', color:sub, margin:'3px 0 0' }}>{calcularAntiguedad(e.fechaIngreso)}</p>
                        </div>
                      </div>
                    </div>
                  );
                })
            }
          </>
        )}

        {/* TAB VENCIMIENTOS */}
        {seccion==='vencimientos'&&(
          vencimientosProximos.length===0
            ? <div style={{ textAlign:'center', padding:'3rem', color:sub }}><p style={{ fontSize:'32px' }}>✅</p><p>Sin vencimientos próximos en los próximos 30 días</p></div>
            : vencimientosProximos.map((v,i)=>{
                const color = v.dias<0?'#e63946':v.dias<=10?'#e63946':v.dias<=30?'#f59e0b':'#22c55e';
                return (
                  <div key={i} style={{ ...card, border:`1px solid ${color}40`, background:v.dias<0?(oscuro?'#1a0505':'#fff5f5'):(oscuro?'#1a0a00':'#fffbeb') }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                      <div>
                        <span style={{ fontSize:'13px', fontWeight:'700', color }}>{v.dias<0?'❌':'⚠️'} {v.label}</span>
                        <p style={{ fontSize:'12px', color:sub, margin:'4px 0 0' }}>{v.empleado} · {v.legajo}</p>
                      </div>
                      <div style={{ textAlign:'right' }}>
                        <span style={{ fontSize:'13px', fontWeight:'700', color }}>{v.dias<0?'Vencido hace '+Math.abs(v.dias)+' días':v.dias+' días'}</span>
                        <p style={{ fontSize:'11px', color:sub, margin:'3px 0 0' }}>{v.fecha}</p>
                      </div>
                    </div>
                  </div>
                );
              })
        )}

        {/* TAB ORGANIGRAMA */}
        {seccion==='organigrama'&&(
          <div>
            {areasEmpresa.filter(a=>empleados.some(e=>e.area===a)).map(a=>{
              const empArea = empleados.filter(e=>e.area===a&&e.estado==='activo');
              return (
                <div key={a} style={{ ...card, marginBottom:'10px' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'10px' }}>
                    <h3 style={{ fontSize:'13px', fontWeight:'700', color:'#7B5BF2', margin:0 }}>{a}</h3>
                    <span style={{ fontSize:'11px', color:sub, background:oscuro?'rgba(123,91,242,.1)':'#f0eeff', padding:'2px 8px', borderRadius:'8px' }}>{empArea.length} activo{empArea.length!==1?'s':''}</span>
                  </div>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:'8px' }}>
                    {empArea.map(e=>(
                      <div key={e.id} onClick={()=>{setDetalle(e);setVista('detalle');setTabDet('datos');cargarRecibos(e.id);}}
                        style={{ padding:'8px 12px', background:oscuro?'#0a1f5c':'#f0eeff', border:'1px solid #7B5BF230', borderRadius:'10px', cursor:'pointer', fontSize:'12px' }}>
                        <p style={{ fontWeight:'700', color:'#7B5BF2', margin:0 }}>{e.nombre}</p>
                        <p style={{ color:sub, margin:'2px 0 0', fontSize:'11px' }}>{e.puesto}</p>
                      </div>
                    ))}
                    {empArea.length===0&&<p style={{ fontSize:'12px', color:sub }}>Sin empleados activos</p>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
